import type { FieldConfigType, FieldType } from "@/schemas/digital-files";
import type { EntityField, FieldTypeName, Section } from "../types";
import { parseCharacters } from "@/utils/format";

export const getFieldTypeInfo = (field: FieldType) => {
  let fieldKey =
    field.configuration?.type?.toLowerCase() ||
    field.type?.toLowerCase() ||
    field.field_type?.toLowerCase() ||
    "text";
  if (field?.configuration?.documentType) {
    fieldKey = "identification";
  }
  const fieldTypeMap: Record<string, { label: string; icon: string }> = {
    text: { label: "Campo de texto", icon: "text" },
    number: { label: "Campo numérico", icon: "hashtag" },
    textarea: { label: "Área de texto (párrafo)", icon: "paragraph" },
    date: { label: "Fecha", icon: "calendar-clock" },
    datetime: { label: "Fecha y hora", icon: "calendar-clock" },
    datetime_range: { label: "Rango de fechas", icon: "calendars" },
    date_range: { label: "Rango de fechas", icon: "calendars" },
    email: { label: "Correo electrónico", icon: "envelope" },
    phone: { label: "Número telefónico", icon: "phone" },
    identification: { label: "Identificación", icon: "id-card" },
    file: { label: "Subida de archivos", icon: "file-arrow-up" },
    switch: { label: "Campo Switch", icon: "toggle-on" },
    select: { label: "Campo Select", icon: "list" },
    multiselect: { label: "Multi-selección", icon: "list" },
    interoperability: { label: "Interoperabilidad", icon: "diagram-nested" },
  };
  return (
    fieldTypeMap[fieldKey] || {
      label: "Campo",
      icon: "question",
    }
  );
};

export const getFieldTypeDB = (field: FieldTypeName) => {
  const fieldTypeMap: Record<FieldTypeName, string> = {
    text: "TEXT",
    number: "NUMBER",
    date: "DATE",
    date_range: "DATE",
    email: "EMAIL",
    phone: "PHONE",
    textarea: "TEXTAREA",
    select: "SELECT",
    multiselect: "MULTISELECT",
    identification: "TEXT",
    file: "URL",
    interoperability: "TEXT",
    switch: "SWITCH",
  };
  return fieldTypeMap[field] || "TEXT";
};

export const getModalValues = (field: FieldType): EntityField => {
  const configuration = field.configuration || {};

  const { type: configType, key, can_expand, ...configFields } = configuration;

  // Usar configType como fuente primaria, fallback a field.type para legacy
  const fieldType = (configType || field.type || "text") as FieldTypeName;

  return {
    id: field.id,
    key: key ?? (field.name ? parseCharacters(field.name.toLowerCase()) : ""),
    type: fieldType,
    name: field.name,
    required: field.is_required,
    defaultValue: field.default_value || undefined,
    // Normalizar can_expand a expandSelectedRecord
    ...(can_expand !== undefined &&
      can_expand !== null && { expandSelectedRecord: can_expand }),
    ...configFields,

    is_array: field?.is_array ?? configFields.is_array ?? false,
    max_items: field?.max_items ?? configFields.max_items ?? null,
  };
};

/** Builds the dropdown-button portion of a field configuration. */
const buildButtonConfig = (args: FieldConfigType) => {
  const {
    buttonEnabled,
    buttonPosition,
    options_source,
    options_table,
    options_column,
    options_dropdown,
  } = args;

  if (!buttonEnabled) {
    return {};
  }

  return {
    buttonEnabled: buttonEnabled,
    buttonPosition: buttonPosition,
    ...(options_source && { options_source: options_source }),
    ...(options_source === "database" && {
      ...(options_table && { options_table: options_table }),
      ...(options_column && { options_column: options_column }),
    }),
    ...(options_source === "manual" &&
      options_dropdown &&
      Array.isArray(options_dropdown) &&
      options_dropdown.length > 0 && {
        options_dropdown: options_dropdown,
      }),
  };
};

/** Builds the select/local-list (options_root) portion of a field configuration. */
const buildOptionsRootConfig = (
  args: FieldConfigType & { options_root_list: string[] },
) => {
  const {
    options_root_source,
    options_root_table,
    options_root_column,
    options_root_list,
  } = args;

  return {
    ...(options_root_source === "database" && {
      ...(options_root_table && { options_root_table: options_root_table }),
      ...(options_root_column && {
        options_root_column: options_root_column,
      }),
    }),
    ...(options_root_source === "manual" &&
      options_root_list &&
      Array.isArray(options_root_list) &&
      options_root_list.length > 0 && {
        options: options_root_list,
      }),
  };
};

export const createField = (
  config: EntityField,
  selectedSection?: Section,
): FieldType => {
  const {
    id,
    type,
    name,
    required,
    defaultValue,
    expandSelectedRecord,
    configuration: entityConfiguration,
    // Extract values for the dropdown button (options)
    options_table,
    options_column,
    options_source,
    options_dropdown,
    buttonEnabled,
    buttonPosition,
    // Extract values for the select/local_list field (options_root)
    options_root_table,
    options_root_column,
    options_root_list,
    options_root_source,
    multipleSelection,
    is_array,
    max_items,
    ...rest
  } = config;

  // Separate configuration properties from top-level normalized properties
  const configurationFields = { ...rest };
  // Remove known top-level properties that shouldn't be in configuration
  delete configurationFields.fieldType;
  delete configurationFields.is_array;
  delete configurationFields.max_items;

  // Flatten entityConfiguration when present (to avoid nesting)
  const flattenedEntityConfig =
    entityConfiguration && typeof entityConfiguration === "object"
      ? entityConfiguration
      : {};

  const finalType =
    multipleSelection && type === "select" ? "multiselect" : type;

  const result: FieldType = {
    id: id || crypto.randomUUID(),
    name,
    field_type: getFieldTypeDB(finalType),
    type: finalType,
    default_value: defaultValue || null,
    is_required: required ?? false,
    order: selectedSection ? selectedSection.fields.length + 1 : 0,
    configuration: {
      ...configurationFields,
      key: config.key,
      type: type,
      section: {
        order: selectedSection?.order || 1,
        title: selectedSection?.title || "",
      },
      ...flattenedEntityConfig,
      // Normalize expandSelectedRecord to can_expand
      ...(expandSelectedRecord !== undefined &&
        expandSelectedRecord !== null && { can_expand: expandSelectedRecord }),
      ...buildButtonConfig({
        buttonEnabled,
        buttonPosition,
        options_source,
        options_table,
        options_column,
        options_dropdown,
      } as FieldConfigType),
      ...buildOptionsRootConfig({
        options_root_source,
        options_root_table,
        options_root_column,
        options_root_list,
      } as FieldConfigType & { options_root_list: string[] }),
      ...(options_root_source ? { options_root_source } : {}),
      ...(multipleSelection !== undefined &&
        multipleSelection !== null && { multipleSelection: multipleSelection }),
    } as FieldConfigType,
    is_array: is_array || false,
    max_items: max_items || undefined,
  } as FieldType;

  return result;
};
